/**
 * Sticky Product Bar
 * A sticky bar that appears at the bottom of the screen when scrolling past the main product information.
 *
 * Performance improvements:
 * - Uses Intersection Observer instead of scroll events for better performance
 * - No more requestAnimationFrame throttling needed
 * - Automatic cleanup and memory management
 * - Precise control over when the bar appears/disappears
 */

class StickyProductBar {
  constructor() {
    this.bar = document.querySelector("[data-sticky-product-bar]");
    this.productInfo = document.querySelector(".product-details");
    this.isVisible = false;
    this.intersectionObserver = null;

    if (!this.bar || !this.productInfo) return;

    this.init();
  }

  init() {
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    // Create intersection observer with 20% threshold
    // This means the sticky bar will show when the product info section
    // is at least 20% out of the viewport (scrolled past)
    //
    // Threshold breakdown:
    // - 0: When the product info section is completely out of view
    // - 0.2: When the product info section is 20% visible (80% scrolled past)
    // - 1: When the product info section is fully visible
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // When the product info section is less than 20% visible (80% scrolled past),
          // show the sticky bar. This provides a smooth transition point.
          if (entry.intersectionRatio < 0.2 && !this.isVisible) {
            this.show();
          } else if (entry.intersectionRatio >= 0.2 && this.isVisible) {
            this.hide();
          }
        });
      },
      {
        threshold: [0, 0.2, 1], // Track 0%, 20%, and 100% visibility
        rootMargin: "0px 0px 0px 0px",
      }
    );

    // Start observing the product information section
    if (this.productInfo) {
      this.intersectionObserver.observe(this.productInfo);
    }
  }

  show() {
    if (!this.bar) return;

    const barElement = this.bar;
    if (barElement instanceof HTMLElement) {
      // Simply add the visible class - CSS handles the animation
      barElement.classList.add("sticky-product-bar--visible");
    }
    this.isVisible = true;
  }

  hide() {
    if (!this.bar) return;

    const barElement = this.bar;
    if (barElement instanceof HTMLElement) {
      // Simply remove the visible class - CSS handles the animation
      barElement.classList.remove("sticky-product-bar--visible");
    }
    this.isVisible = false;
  }

  // Clean up observer when component is destroyed
  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new StickyProductBar());
} else {
  new StickyProductBar();
}
